import { Link } from "react-router-dom";
import { ShieldCheck, Clock, Truck } from "lucide-react";
import Layout from "./Layout";
import SEOHead from "./SEOHead";
import Breadcrumb from "./Breadcrumb";
import FAQSection from "./FAQSection";
import { business } from "../config/business";

const generalFaqs = [
  { q: "Where is TR Structures based?", a: "We're based in Bibinagar, Hyderabad, Telangana — this is our only physical facility. We design and fabricate at this location and deliver, transport and erect structures across Telangana and Andhra Pradesh." },
  { q: "What does TR Structures build?", a: "PEB (Pre-Engineered Building) structures, industrial sheds, structural steel fabrication, prefabricated buildings, and roof & wall sheeting for industrial, commercial and agricultural applications." },
  { q: "How do I request a quote?", a: "Use our enquiry form, call us, or message us on WhatsApp with your project details — dimensions, intended use and location. We'll follow up with a design consultation." },
];

const processFaqs = [
  { q: "How long does a typical PEB project take?", a: "This varies with size and complexity, but PEB construction typically completes significantly faster than equivalent RCC construction — a mid-size industrial shed often completes in a few months from finalized design to handover." },
  { q: "Do you handle the full project from design to erection?", a: "Yes — our scope covers design, fabrication, transportation and on-site erection, coordinated as one project." },
  { q: "Can you visit our site before quoting?", a: "For projects of meaningful size, a site visit is recommended and can be arranged, particularly for locations within reasonable distance of our Hyderabad facility." },
];

const pricingFaqs = [
  { q: "How much does a PEB structure cost?", a: "It depends heavily on span, height, flooring, insulation and crane requirements. Use our PEB Cost Calculator for an indicative range, then request a project-specific quote for accurate pricing." },
  { q: "Is the price you quote final?", a: "Quotes are based on the specifications discussed at the time — any changes to design, materials or scope after that point may affect final pricing, which we'll always confirm before proceeding." },
];

const GeneralFAQ = () => {
  return (
    <Layout>
      <SEOHead
        title="Frequently Asked Questions | TR Structures"
        description="Common questions about TR Structures' PEB, industrial shed and steel fabrication services, pricing and project process."
        keywords="TR Structures FAQ, PEB questions, industrial shed FAQ"
      />
      <Breadcrumb items={[{ name: "FAQ" }]} />

      {/* 1. Hero */}
      <section className="section-padding pb-8">
        <div className="container-custom text-center">
          <h1 className="font-montserrat text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Frequently Asked <span className="text-secondary">Questions</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Common questions about our services, process and pricing. For anything else, reach out directly.
          </p>
        </div>
      </section>

      {/* 2. Trust strip */}
      <section className="bg-primary py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
            <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Design to erection, one team</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Fast, transparent process</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Truck size={18} aria-hidden="true" /> Telangana & AP delivery</span>
          </div>
        </div>
      </section>

      {/* 3-5. FAQ groups */}
      <FAQSection faqs={generalFaqs} title="General" />
      <FAQSection faqs={processFaqs} title="Project Process" />
      <FAQSection faqs={pricingFaqs} title="Pricing" />

      {/* 6. CTA */}
      <section className="section-padding bg-secondary">
        <div className="container-custom text-center">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
            Still Have Questions?
          </h2>
          <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
            Reach out directly and our team will get back to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary inline-block px-8 py-4">Contact Us</Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="btn-outline border-secondary-foreground text-secondary-foreground hover:bg-secondary-foreground hover:text-secondary inline-block px-8 py-4">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default GeneralFAQ;
